import { create } from 'zustand';
import { PaperTradingConfig, PaperOrder, PaperPosition, PendingOrder } from '@shared/types';

// Default configuration for MNQ futures
const DEFAULT_CONFIG: PaperTradingConfig = {
  initialBalance: 100000,
  commission: 2.50, // Per contract
  slippageTicks: 1, // 1 tick slippage
  tickSize: 0.25, // MNQ tick size (0.25 points)
  tickValue: 0.5, // $0.50 per tick for MNQ ($2 per point / 4 ticks per point)
  maxPositionSize: 10, // Max 10 contracts
  enableSlippage: true,
  enableCommission: true,
};

interface TradeStats {
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  largestWin: number;
  largestLoss: number;
}

interface PaperTradingState {
  // Configuration
  config: PaperTradingConfig;
  enabled: boolean;

  // Mode tracking
  isBacktestMode: boolean;
  currentBacktestSessionId: string | null;

  // Account state
  balance: number;
  initialBalance: number;
  equity: number;

  // Trading state
  orders: PaperOrder[];
  pendingOrders: PendingOrder[];
  positions: PaperPosition[];

  // P&L tracking
  dailyPnL: number;
  totalPnL: number;
  realizedPnL: number;
  unrealizedPnL: number;

  // Statistics
  stats: TradeStats;

  // Actions - Configuration
  setConfig: (config: Partial<PaperTradingConfig>) => void;
  enable: () => void;
  disable: () => void;
  toggleEnabled: () => void;

  // Actions - Mode
  setBacktestMode: (enabled: boolean, sessionId?: string) => void;
  endBacktestSession: () => void;

  // Actions - Orders
  addOrder: (order: PaperOrder) => void;
  updateOrder: (id: string, updates: Partial<PaperOrder>) => void;
  removeOrder: (id: string) => void;
  clearOrders: () => void;

  // Actions - Pending Orders
  addPendingOrder: (order: PendingOrder) => void;
  updatePendingOrder: (id: string, updates: Partial<PendingOrder>) => void;
  removePendingOrder: (id: string) => void;
  clearPendingOrders: () => void;

  // Actions - Positions
  setPosition: (position: PaperPosition) => void;
  updatePosition: (symbol: string, updates: Partial<PaperPosition>) => void;
  closePosition: (symbol: string) => void;
  clearPositions: () => void;

  // Actions - Account
  setBalance: (balance: number) => void;
  updateBalance: (amount: number) => void;
  updateEquity: (equity: number) => void;
  setRealizedPnL: (pnl: number) => void;
  updateUnrealizedPnL: (pnl: number) => void;
  addRealizedPnL: (pnl: number) => void;

  // Actions - Stats
  setStats: (stats: TradeStats) => void;
  recordTrade: (pnl: number) => void;
  calculateStats: () => void;

  // Actions - Reset
  resetAccount: () => void;
  reset: () => void;
}

export const usePaperTradingStore = create<PaperTradingState>((set, get) => ({
  // Initial state
  config: DEFAULT_CONFIG,
  enabled: true,
  isBacktestMode: false,
  currentBacktestSessionId: null,
  balance: DEFAULT_CONFIG.initialBalance,
  initialBalance: DEFAULT_CONFIG.initialBalance,
  equity: DEFAULT_CONFIG.initialBalance,
  orders: [],
  pendingOrders: [],
  positions: [],
  dailyPnL: 0,
  totalPnL: 0,
  realizedPnL: 0,
  unrealizedPnL: 0,
  stats: {
    totalTrades: 0,
    winningTrades: 0,
    losingTrades: 0,
    winRate: 0,
    largestWin: 0,
    largestLoss: 0,
  },

  // Configuration actions
  setConfig: (newConfig: Partial<PaperTradingConfig>) =>
    set((state) => ({
      config: { ...state.config, ...newConfig },
    })),

  enable: () => set({ enabled: true }),
  disable: () => set({ enabled: false }),
  toggleEnabled: () => set((state) => ({ enabled: !state.enabled })),

  // Mode actions
  setBacktestMode: (enabled: boolean, sessionId?: string) =>
    set({
      isBacktestMode: enabled,
      currentBacktestSessionId: sessionId || null,
    }),

  endBacktestSession: () =>
    set({
      isBacktestMode: false,
      currentBacktestSessionId: null,
    }),

  // Order actions
  addOrder: (order: PaperOrder) =>
    set((state) => ({
      orders: [...state.orders, order],
    })),

  updateOrder: (id: string, updates: Partial<PaperOrder>) =>
    set((state) => ({
      orders: state.orders.map((o) => (o.id === id ? { ...o, ...updates } : o)),
    })),

  removeOrder: (id: string) =>
    set((state) => ({
      orders: state.orders.filter((o) => o.id !== id),
    })),

  clearOrders: () => set({ orders: [] }),

  // Pending order actions
  addPendingOrder: (order: PendingOrder) =>
    set((state) => ({
      pendingOrders: [...state.pendingOrders, order],
    })),

  updatePendingOrder: (id: string, updates: Partial<PendingOrder>) =>
    set((state) => ({
      pendingOrders: state.pendingOrders.map((o) =>
        o.id === id ? { ...o, ...updates } : o
      ),
    })),

  removePendingOrder: (id: string) =>
    set((state) => ({
      pendingOrders: state.pendingOrders.filter((o) => o.id !== id),
    })),

  clearPendingOrders: () => set({ pendingOrders: [] }),

  // Position actions
  setPosition: (position: PaperPosition) =>
    set((state) => {
      const existingIndex = state.positions.findIndex(
        (p) => p.symbol === position.symbol
      );

      if (existingIndex >= 0) {
        // Update existing position
        const newPositions = [...state.positions];
        newPositions[existingIndex] = position;
        return { positions: newPositions };
      } else {
        // Add new position
        return { positions: [...state.positions, position] };
      }
    }),

  updatePosition: (symbol: string, updates: Partial<PaperPosition>) =>
    set((state) => ({
      positions: state.positions.map((p) =>
        p.symbol === symbol ? { ...p, ...updates } : p
      ),
    })),

  closePosition: (symbol: string) =>
    set((state) => ({
      positions: state.positions.filter((p) => p.symbol !== symbol),
    })),

  clearPositions: () => set({ positions: [] }),

  // Account actions
  setBalance: (balance: number) =>
    set({ balance }),

  updateBalance: (amount: number) =>
    set((state) => ({
      balance: state.balance + amount,
    })),

  updateEquity: (equity: number) =>
    set({ equity }),

  setRealizedPnL: (pnl: number) =>
    set({ realizedPnL: pnl }),

  updateUnrealizedPnL: (pnl: number) =>
    set((state) => {
      const newEquity = state.balance + pnl;
      return {
        unrealizedPnL: pnl,
        equity: newEquity,
      };
    }),

  addRealizedPnL: (pnl: number) =>
    set((state) => ({
      realizedPnL: state.realizedPnL + pnl,
      totalPnL: state.totalPnL + pnl,
      dailyPnL: state.dailyPnL + pnl,
    })),

  // Stats actions
  setStats: (stats: TradeStats) =>
    set({ stats }),

  recordTrade: (pnl: number) =>
    set((state) => {
      const isWin = pnl > 0;
      const newStats = {
        totalTrades: state.stats.totalTrades + 1,
        winningTrades: state.stats.winningTrades + (isWin ? 1 : 0),
        losingTrades: state.stats.losingTrades + (isWin ? 0 : 1),
        winRate: 0, // Will be calculated
        largestWin: Math.max(state.stats.largestWin, pnl),
        largestLoss: Math.min(state.stats.largestLoss, pnl),
      };

      newStats.winRate =
        newStats.totalTrades > 0
          ? (newStats.winningTrades / newStats.totalTrades) * 100
          : 0;

      return { stats: newStats };
    }),

  calculateStats: () =>
    set((state) => {
      const stats = { ...state.stats };
      stats.winRate =
        stats.totalTrades > 0
          ? (stats.winningTrades / stats.totalTrades) * 100
          : 0;
      return { stats };
    }),

  // Reset actions
  resetAccount: () =>
    set((state) => ({
      balance: state.config.initialBalance,
      initialBalance: state.config.initialBalance,
      equity: state.config.initialBalance,
      orders: [],
      pendingOrders: [],
      positions: [],
      dailyPnL: 0,
      totalPnL: 0,
      realizedPnL: 0,
      unrealizedPnL: 0,
      stats: {
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        largestWin: 0,
        largestLoss: 0,
      },
    })),

  reset: () =>
    set({
      config: DEFAULT_CONFIG,
      enabled: true,
      balance: DEFAULT_CONFIG.initialBalance,
      initialBalance: DEFAULT_CONFIG.initialBalance,
      equity: DEFAULT_CONFIG.initialBalance,
      orders: [],
      pendingOrders: [],
      positions: [],
      dailyPnL: 0,
      totalPnL: 0,
      realizedPnL: 0,
      unrealizedPnL: 0,
      stats: {
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        largestWin: 0,
        largestLoss: 0,
      },
    }),
}));
